"use client";

import React from "react";
import { motion } from "framer-motion";
import { UserCheck, BadgePoundSterling, Zap, Target, ChartBar as BarChart3, ShieldCheck, Check, X, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const benefits = [{
  id: 1,
  title: "Direct Access to Senior Expertise",
  badge: "100% Senior Execution",
  consultant: "You work directly with me, Yassine Essaadi. Every strategy, technical fix, and content audit is handled by an experienced consultant.",
  agency: "You are pitched by senior founders, then passed off to junior account managers or trainees.",
  iconKey: "user"
}, {
  id: 2,
  title: "Cost Efficiency & Zero Overhead",
  badge: "Maximum Value Per £ Spent",
  consultant: "Every pound of your budget goes directly into high-impact SEO execution, strategy, and technical work.",
  agency: "A large portion of your fee covers expensive city offices, sales commissions, and account managers.",
  iconKey: "sterling"
}, {
  id: 3,
  title: "Agile Execution & Fast Turnaround",
  badge: "No Corporate Bottlenecks",
  consultant: "Direct communication with rapid implementation. Technical fixes and strategy updates happen in days, not weeks.",
  agency: "Slow decision-making layers, ticketing queues, and bureaucratic delays for simple site changes.",
  iconKey: "zap"
}, {
  id: 4,
  title: "Tailored SEO Built For Your Goals",
  badge: "Custom Growth Roadmap",
  consultant: "Custom strategy designed specifically for your UK, national, or international market needs.",
  agency: "Recycled generic checklist templates and standardized monthly deliverable packages.",
  iconKey: "target"
}, {
  id: 5,
  title: "Transparent & Honest Reporting",
  badge: "Focus on Revenue & Leads",
  consultant: "Clear, plain-English reports focused on organic revenue, lead quality, and tangible rankings.",
  agency: "Automated monthly PDF dumps filled with vanity metrics that obscure actual business growth.",
  iconKey: "chart"
}, {
  id: 6,
  title: "Direct Personal Accountability",
  badge: "Personal Commitment",
  consultant: "Your search performance directly impacts my reputation. I am fully invested in your success.",
  agency: "High staff turnover and diluted responsibility where your account is one of dozens in a queue.",
  iconKey: "shield"
}];
function getBenefitIcon(iconKey: string) {
  switch (iconKey) {
    case "user":
      return <UserCheck className="size-5 text-primary" />;
    case "sterling":
      return <BadgePoundSterling className="size-5 text-primary" />;
    case "zap":
      return <Zap className="size-5 text-primary" />;
    case "target":
      return <Target className="size-5 text-primary" />;
    case "chart":
      return <BarChart3 className="size-5 text-primary" />;
    case "shield":
      return <ShieldCheck className="size-5 text-primary" />;
    default:
      return <Sparkles className="size-5 text-primary" />;
  }
}
export default function ConsultantBenefits() {
  return <section id="consultantvsagencybenefits" data-nav="light" className="relative bg-background py-20 lg:py-28 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <Badge variant="secondary" className="px-3 py-1 text-xs sm:text-sm font-medium tracking-wide uppercase bg-secondary text-secondary-foreground rounded-full border border-accent/20">
            <Sparkles className="size-3.5 mr-1 text-accent inline-block" />
            Direct Consultant Advantage
          </Badge>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground font-default">
            Why Work Directly With Me vs. Hiring an SEO Agency
          </h2>

          <p className="text-base md:text-lg text-muted-foreground leading-relaxed font-default pt-2">
            When you hire YE Consultancy, you don't get passed down to a junior account manager.
            You gain direct access to my senior hands-on expertise, personal accountability,
            and tailored SEO strategies designed for sustainable search growth.
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-16">
          {benefits.map((item, i) => <motion.div key={item.id} data-index={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.08
        }} className="flex">
              <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-[box-shadow,border-color] duration-300 rounded-2xl overflow-hidden">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <div className="p-2.5 rounded-xl bg-muted/80 border border-border shrink-0">
                      {getBenefitIcon(item.iconKey)}
                    </div>
                    <Badge variant="outline" className="bg-secondary/60 text-foreground border-accent/30 text-[13px] font-medium py-0.5 rounded-full">
                      {item.badge}
                    </Badge>
                  </div>
                  <CardTitle className="text-xl font-semibold text-foreground font-default leading-snug">
                    {item.title}
                  </CardTitle>
                </CardHeader>

                <CardContent className="space-y-4 flex-grow">
                  <div className="space-y-2 p-3.5 rounded-xl bg-background/80 border border-border/60">
                    <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-primary">
                      <Check className="size-4 text-primary shrink-0" />
                      <span>With Me (Yassine)</span>
                    </div>
                    <p className="text-sm text-foreground/90 leading-relaxed font-default">
                      {item.consultant}
                    </p>
                  </div>

                  <div className="space-y-2 p-3.5 rounded-xl bg-muted/40 border border-border/40 opacity-80">
                    <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      <X className="size-4 text-destructive/80 shrink-0" />
                      <span>Typical SEO Agency</span>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed font-default">
                      {item.agency}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </div>

        {/* Detailed Comparison Callout */}
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="rounded-3xl border border-border bg-card p-6 md:p-10 shadow-md mb-16">
          <div className="max-w-3xl mx-auto text-center mb-8">
            <h3 className="text-2xl md:text-3xl font-semibold text-foreground font-default mb-3">
              The Real Difference in Partnership
            </h3>
            <p className="text-sm md:text-base text-muted-foreground font-default">
              My business model is built on quality of execution rather than volume of clients. You work directly with me every step of the way.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 items-stretch">
            {/* Freelance Consultant Column */}
            <div className="p-6 rounded-2xl bg-secondary/30 border border-accent/30 flex flex-col justify-between">
              <div className="space-y-4">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 border border-accent/30 text-foreground font-semibold text-sm">
                  <Check className="size-4 text-accent" />
                  Working With Yassine (Freelance Consultant)
                </div>
                <ul className="space-y-3 pt-2 text-sm text-foreground font-default">
                  <li className="flex items-start gap-2.5">
                    <Check className="size-4 text-primary shrink-0 mt-0.5" />
                    <span>Direct phone, email, and Slack access to your senior strategist.</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <Check className="size-4 text-primary shrink-0 mt-0.5" />
                    <span>Custom SEO roadmap built specifically for your revenue growth.</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <Check className="size-4 text-primary shrink-0 mt-0.5" />
                    <span>Agile turnaround with technical fixes applied without bureaucracy.</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <Check className="size-4 text-primary shrink-0 mt-0.5" />
                    <span>Honest insights, direct feedback, and realistic projections.</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Traditional Agency Column */}
            <div className="p-6 rounded-2xl bg-muted/50 border border-border/60 flex flex-col justify-between">
              <div className="space-y-4">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-muted border border-border text-muted-foreground font-semibold text-sm">
                  <X className="size-4 text-destructive/80" />
                  Traditional SEO Agency
                </div>
                <ul className="space-y-3 pt-2 text-sm text-muted-foreground font-default">
                  <li className="flex items-start gap-2.5">
                    <X className="size-4 text-destructive/70 shrink-0 mt-0.5" />
                    <span>Account managed by junior staff juggling 20+ accounts simultaneously.</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <X className="size-4 text-destructive/70 shrink-0 mt-0.5" />
                    <span>Standardized task checklists designed for agency efficiency, not yours.</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <X className="size-4 text-destructive/70 shrink-0 mt-0.5" />
                    <span>Slow turnaround caused by internal approvals and multi-tier hierarchies.</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <X className="size-4 text-destructive/70 shrink-0 mt-0.5" />
                    <span>Automated reports heavy on vanity metrics and keyword fluff.</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Bottom CTA Banner */}
        <div className="bg-primary text-primary-foreground rounded-3xl p-8 md:p-12 shadow-xl text-center max-w-5xl mx-auto flex flex-col items-center gap-6">
          <div className="max-w-2xl space-y-3">
            <h3 className="text-2xl md:text-3xl font-semibold tracking-tight font-default text-primary-foreground">
              Ready for a Tailored SEO Strategy Without Agency Fluff?
            </h3>
            <p className="text-primary-foreground/90 text-sm md:text-base font-default leading-relaxed">
              Book a direct consultation with me today. We'll examine your website's search performance and identify immediate growth opportunities.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-4 pt-2">
            <Button asChild size="lg" className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold px-6 py-6 rounded-xl shadow-md transition-[background-color,box-shadow]">
              <Link to="/ContactPage">
                Book a Free SEO Consultation
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>

            <Button asChild variant="outline" size="lg" className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 font-medium px-6 py-6 rounded-xl bg-transparent">
              <Link to="/CaseStudiesPage">
                Explore My Case Studies
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}